/**
 * Facility create/edit form validations (user story).
 * Only rules for fields that exist in the facility flows — no new fields added.
 */

import * as Yup from 'yup';
import { FACILITY_CODE_MAX_LENGTH, type FacilityFormValues } from '../utils/facilityMapper';
import { isWellFormedEmailAddress } from './emailValidation';

/** Facility Name — letters and spaces, 3–100 */
const FACILITY_NAME_REGEX = /^[A-Za-z ]+$/;
const FACILITY_NAME_MSG = 'Only letters and spaces are allowed. Minimum 3 characters.';

/** Facility Code — uppercase A–Z and 0–9, 3–20 (uniqueness: server) */
const FACILITY_CODE_REGEX = /^[A-Z0-9]+$/;
const FACILITY_CODE_MSG = `Use 3–${FACILITY_CODE_MAX_LENGTH} uppercase letters or numbers only. Must be unique.`;
const FACILITY_CODE_MAX_MSG = `Facility code must be at most ${FACILITY_CODE_MAX_LENGTH} characters.`;

const FACILITY_TYPE_VALUES = ['Hospital', 'Diagnostic Center', 'Clinic', 'HUB', 'SPOKE'] as const;
const FACILITY_STATUS_VALUES = ['Active', 'Inactive', 'Suspended'] as const;
const INTEGRATION_STATUS_VALUES = ['Connected', 'Pending', 'Failed'] as const;
const REPORT_APPROVAL_VALUES = ['Yes', 'No'] as const;

/** Description — optional; letters, numbers, spaces, . , - */
const DESCRIPTION_REGEX = /^[A-Za-z0-9 .,-]*$/;
const DESCRIPTION_MAX_MSG = 'Maximum 250 characters allowed.';
const DESCRIPTION_CHARS_MSG =
  'Only letters, numbers, spaces, and the characters . , - are allowed in the description.';

/** Address — letters, numbers, spaces, , . / - */
const ADDRESS_REGEX = /^[A-Za-z0-9 ,./-]+$/;
const ADDRESS_LINE1_MSG = 'Enter valid address (min 5 characters).';
const ADDRESS_LINE2_MAX = 'Maximum 150 characters allowed.';

const PHONE_IN_REGEX = /^[6-9][0-9]{9}$/;
const PHONE_IN_MSG = 'Enter a valid 10-digit mobile number.';

const EMAIL_MSG = 'Enter a valid email address.';

const PINCODE_REGEX = /^[1-9][0-9]{5}$/;
const PINCODE_MSG = 'Enter a valid 6-digit pincode.';

const AE_TITLE_REGEX = /^[A-Z0-9_]+$/;
const AE_TITLE_MSG = 'AE Title must be uppercase and max 16 characters.';

const IPV4_REGEX =
  /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IPV4_MSG = 'Enter a valid IP address.';

const PORT_RANGE_MSG = 'Port must be between 1024 and 65535.';

const EMPLOYEE_ID_REGEX = /^[A-Za-z0-9]+$/;
const EMPLOYEE_ID_MSG = 'Employee ID must be alphanumeric (max 20 characters).';

const PERSON_NAME_REGEX = /^[A-Za-z ]+$/;
const FULL_NAME_MSG = 'Only letters and spaces allowed.';

const USERNAME_REGEX = /^[a-z0-9._]+$/;
const USERNAME_MSG = 'Username must be lowercase and unique.';

const DESIGNATION_REGEX = /^[A-Za-z ]*$/;
const DESIGNATION_MSG = 'Designation may only contain letters and spaces (max 50 characters).';

const FACILITY_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;
const FACILITY_PASSWORD_MSG =
  'Password must be at least 8 characters with upper, lower, number, and special character.';

function facilityNameYup() {
  return Yup.string()
    .trim()
    .required('Facility Name is required')
    .min(3, FACILITY_NAME_MSG)
    .max(100, FACILITY_NAME_MSG)
    .matches(FACILITY_NAME_REGEX, FACILITY_NAME_MSG);
}

function facilityCodeYup() {
  return Yup.string()
    .trim()
    .required('Facility Code is required')
    .min(3, FACILITY_CODE_MSG)
    .max(FACILITY_CODE_MAX_LENGTH, FACILITY_CODE_MAX_MSG)
    .matches(FACILITY_CODE_REGEX, FACILITY_CODE_MSG);
}

function facilityTypeYup() {
  return Yup.string()
    .trim()
    .required('Please select a facility type.')
    .oneOf([...FACILITY_TYPE_VALUES], 'Please select a facility type.');
}

function descriptionYup() {
  return Yup.string()
    .trim()
    .max(250, DESCRIPTION_MAX_MSG)
    .test('description-chars', DESCRIPTION_CHARS_MSG, value => {
      if (!value) {
        return true;
      }
      return DESCRIPTION_REGEX.test(value);
    });
}

function facilityPhoneYup(label: string) {
  return Yup.string()
    .transform(v => (v == null ? '' : String(v).replace(/\D/g, '').slice(-10)))
    .trim()
    .required(`${label} is required`)
    .matches(PHONE_IN_REGEX, PHONE_IN_MSG);
}

function facilityEmailYup(label: string) {
  const requiredMsg = `${label} is required`;
  return Yup.string()
    .trim()
    .required(requiredMsg)
    .test('email-general', EMAIL_MSG, v => isWellFormedEmailAddress(String(v ?? '').trim()));
}

function statusYup() {
  return Yup.string()
    .trim()
    .required('Please select status.')
    .oneOf([...FACILITY_STATUS_VALUES], 'Please select status.');
}

const APPROVED_BY_VALUES = ['Senior Radiologist', 'QC Technician'] as const;

/** Admin Facility Details view — matches legacy "Report Approval" copy. */
function reportApprovalYup() {
  return Yup.string()
    .trim()
    .required('Please select Report Approval Required.')
    .oneOf([...REPORT_APPROVAL_VALUES], 'Please select Report Approval Required.');
}

/** Add Facility / superadmin dialog — dual-stage approval field copy. */
function dualStageReportApprovalYup() {
  return Yup.string()
    .trim()
    .required('Please select Dual stage report approval.')
    .oneOf([...REPORT_APPROVAL_VALUES], 'Please select Dual stage report approval.');
}

function approvedByWhenDualStageYup() {
  return Yup.string()
    .trim()
    .when('hasRadiologistHierarchy', {
      is: 'Yes',
      then: schema =>
        schema
          .required('Please select Approved by.')
          .oneOf([...APPROVED_BY_VALUES], 'Please select Approved by.'),
      otherwise: schema => schema.notRequired(),
    });
}

function addressLine1Yup() {
  return Yup.string()
    .trim()
    .required('Address Line 1 is required')
    .min(5, ADDRESS_LINE1_MSG)
    .max(150, ADDRESS_LINE1_MSG)
    .matches(ADDRESS_REGEX, ADDRESS_LINE1_MSG);
}

function addressLine2Yup() {
  return Yup.string()
    .trim()
    .max(150, ADDRESS_LINE2_MAX)
    .test('addressLine2-chars', ADDRESS_LINE1_MSG, value => {
      if (!value) {
        return true;
      }
      return ADDRESS_REGEX.test(value);
    });
}

function stateYup() {
  return Yup.string().trim().required('Please select a state.');
}

function cityYup() {
  return Yup.string().trim().required('Please select a city.');
}

function countryYup() {
  return Yup.string().trim().required('Country is required');
}

function pincodeYup() {
  return Yup.string().trim().required('Pincode is required').matches(PINCODE_REGEX, PINCODE_MSG);
}

function pacsAeTitleYup() {
  return Yup.string()
    .trim()
    .required('PACS AE Title is required')
    .max(16, AE_TITLE_MSG)
    .matches(AE_TITLE_REGEX, AE_TITLE_MSG);
}

function pacsIpYup() {
  return Yup.string().trim().required('PACS IP Address is required').matches(IPV4_REGEX, IPV4_MSG);
}

function pacsPortYup() {
  return Yup.string()
    .trim()
    .required('PACS Port is required')
    .matches(/^\d+$/, 'Port must be numeric')
    .test('pacs-port-range', PORT_RANGE_MSG, value => {
      if (!value) {
        return false;
      }
      const n = parseInt(value, 10);
      return n >= 1024 && n <= 65535;
    });
}

function integrationStatusYup() {
  return Yup.string()
    .trim()
    .required('Integration status is required')
    .oneOf([...INTEGRATION_STATUS_VALUES], 'Invalid integration status.');
}

function employeeIdYup() {
  return Yup.string()
    .trim()
    .required('Employee ID is required')
    .max(20, EMPLOYEE_ID_MSG)
    .matches(EMPLOYEE_ID_REGEX, EMPLOYEE_ID_MSG);
}

function fullNameYup() {
  return Yup.string()
    .trim()
    .required('Full Name is required')
    .min(3, FULL_NAME_MSG)
    .max(100, FULL_NAME_MSG)
    .matches(PERSON_NAME_REGEX, FULL_NAME_MSG);
}

function genderYup() {
  return Yup.string()
    .trim()
    .required('Gender is required')
    .oneOf(['Male', 'Female', 'Other'], 'Gender is required');
}

function usernameYup() {
  return Yup.string()
    .trim()
    .required('Username is required')
    .min(5, USERNAME_MSG)
    .max(30, USERNAME_MSG)
    .matches(USERNAME_REGEX, USERNAME_MSG);
}

function designationYup() {
  return Yup.string()
    .trim()
    .max(50, DESIGNATION_MSG)
    .test('designation-chars', DESIGNATION_MSG, value => {
      if (!value) {
        return true;
      }
      return DESIGNATION_REGEX.test(value);
    });
}

function passwordCreateYup() {
  return Yup.string()
    .trim()
    .required('Password is required')
    .matches(FACILITY_PASSWORD_REGEX, FACILITY_PASSWORD_MSG);
}

/** Full Add Facility / edit page schema (includes PACS, report approval, admin block rules). */
export function getAddFacilityValidationSchema(isEdit: boolean, isAdminView: boolean) {
  const facilityShape: Record<string, Yup.AnySchema> = {
    name: facilityNameYup(),
    code: facilityCodeYup(),
    type: facilityTypeYup(),
    description: descriptionYup(),
    phoneNo: facilityPhoneYup('Phone No.'),
    emailId: facilityEmailYup('Email ID'),
    addressLine1: addressLine1Yup(),
    addressLine2: addressLine2Yup(),
    city: cityYup(),
    state: stateYup(),
    country: countryYup(),
    pincode: pincodeYup(),
    pacsAETitle: pacsAeTitleYup(),
    pacsIPAddress: pacsIpYup(),
    pacsPort: pacsPortYup(),
    integrationStatus: integrationStatusYup(),
    status: statusYup(),
    hasRadiologistHierarchy: dualStageReportApprovalYup(),
    seniorRadiologistReviewRequired: approvedByWhenDualStageYup(),
  };

  if (!isAdminView) {
    facilityShape.employeeId = employeeIdYup();
    facilityShape.fullName = fullNameYup();
    facilityShape.adminPhoneNo = facilityPhoneYup('Phone No.');
    facilityShape.adminEmailId = facilityEmailYup('Email ID');
    facilityShape.gender = genderYup();
    facilityShape.username = usernameYup();
    facilityShape.designation = designationYup();
    facilityShape.password = isEdit ? Yup.string().trim().notRequired() : passwordCreateYup();
  } else {
    facilityShape.employeeId = Yup.string().trim().notRequired();
    facilityShape.fullName = Yup.string().trim().notRequired();
    facilityShape.adminPhoneNo = Yup.string().trim().notRequired();
    facilityShape.adminEmailId = Yup.string().trim().notRequired();
    facilityShape.gender = Yup.string().trim().notRequired();
    facilityShape.username = Yup.string().trim().notRequired();
    facilityShape.designation = Yup.string().trim().notRequired();
    facilityShape.password = Yup.string().trim().notRequired();
  }

  return Yup.object().shape(facilityShape);
}

/** Create/Edit Facility dialog (superadmin modal). */
export function getFacilityCreateEditDialogSchemas(isEdit: boolean) {
  const facilityDetailsShape = {
    name: facilityNameYup(),
    code: facilityCodeYup(),
    type: facilityTypeYup(),
    description: descriptionYup(),
    status: statusYup(),
    hasRadiologistHierarchy: dualStageReportApprovalYup(),
    seniorRadiologistReviewRequired: approvedByWhenDualStageYup(),
    addressLine1: addressLine1Yup(),
    addressLine2: addressLine2Yup(),
    city: cityYup(),
    state: stateYup(),
    country: countryYup(),
    pincode: pincodeYup(),
    phoneNo: facilityPhoneYup('Contact Number'),
    emailId: facilityEmailYup('Email ID'),
  };

  const adminUserShape = {
    employeeId: employeeIdYup(),
    fullName: fullNameYup(),
    adminPhoneNo: facilityPhoneYup('Phone No.'),
    adminEmailId: facilityEmailYup('Email ID'),
    gender: genderYup(),
    username: usernameYup(),
    password: isEdit ? Yup.string().trim().notRequired() : passwordCreateYup(),
  };

  return {
    facilityDetailsSchema: Yup.object().shape(facilityDetailsShape),
    schema: Yup.object().shape({ ...facilityDetailsShape, ...adminUserShape }),
  };
}

/** Admin Facility Details — Basic tab only (PACS tab not active in UI). */
const facilityAdminDetailsViewDraftSchema = Yup.object({
  name: facilityNameYup(),
  type: facilityTypeYup(),
  description: descriptionYup(),
  addressLine1: addressLine1Yup(),
  addressLine2: addressLine2Yup(),
  state: stateYup(),
  city: cityYup(),
  pincode: pincodeYup(),
  phoneNo: facilityPhoneYup('Contact number'),
  emailId: facilityEmailYup('Email'),
  hasRadiologistHierarchy: reportApprovalYup(),
});

/** Admin Facility Details view — inline draft (no facility code edit, no admin user block). */
export function validateFacilityDetailsViewDraft(draft: FacilityFormValues): {
  valid: boolean;
  fields: Partial<Record<string, string>>;
} {
  const fields: Partial<Record<string, string>> = {};

  try {
    facilityAdminDetailsViewDraftSchema.validateSync(
      {
        name: draft.name,
        type: draft.type,
        description: draft.description ?? '',
        addressLine1: draft.addressLine1,
        addressLine2: draft.addressLine2 ?? '',
        state: draft.state,
        city: draft.city,
        pincode: draft.pincode,
        phoneNo: draft.phoneNo,
        emailId: draft.emailId,
        hasRadiologistHierarchy: draft.hasRadiologistHierarchy ?? 'No',
      },
      { abortEarly: false }
    );
  } catch (e) {
    if (e instanceof Yup.ValidationError) {
      const inner = e.inner.length ? e.inner : [e];
      for (const err of inner) {
        if (err.path) {
          fields[err.path] = err.message;
        }
      }
    }
  }

  return { valid: Object.keys(fields).length === 0, fields };
}
